"use client";

import React, { type Dispatch, type SetStateAction, useState } from "react";
import ArrowRight from "~/assets/icons/arrow-right.svg";
import Icon from "~/components/ui/Icon";
import LinkArrow from "~/assets/icons/link-arrow.svg";
import GuestIcon from "~/assets/icons/navstevy.svg";
import Moment from "moment";
import { useFormContext } from "react-hook-form";
import { AiFillExclamationCircle } from "react-icons/ai";
import { IoIosCheckmarkCircle } from "react-icons/io";
import type { components } from "~/types/api-types";
import SuccessFormCard from "~/components/ui/SuccessFormCard";
import ErrorFormCard from "~/components/ui/ErrorFormCard";
import { FullscreenModalContentWrapper } from "~/components/ui/FullscreenModal";

export type MessageFormInput = {
  name: string;
  subtitle?: string;
  message: string;
};

export default function MessageForm({
  message,
  isSubmitted,
  hasError,
  setErrorAction,
  closeModalAction,
  success,
}: {
  message: Comment | null;
  isSubmitted: boolean;
  hasError?: boolean;
  setErrorAction: Dispatch<SetStateAction<boolean | null>>;
  closeModalAction: () => void;
  success: boolean;
}) {
  const {
    register,
    getFieldState,
    watch,
    formState,
    formState: { errors },
  } = useFormContext<MessageFormInput>();

  if (success) {
    return (
      <>
        <FullscreenModalContentWrapper shouldShift={true}>
          <SuccessFormCard
            closeModalAction={closeModalAction}
            message={"Odpoveď byla úspěšně přidána."}
            buttonMessage={"Zpět na návštěvy"}
          />
        </FullscreenModalContentWrapper>
      </>
    );
  }

  return (
    <>
      <FullscreenModalContentWrapper shouldShift={!!message}>
        {!success && hasError && (
          <ErrorFormCard setErrorAction={setErrorAction} />
        )}
        {message && (
          <div className="mb-6">
            <CommentCollapseCard message={message} />
          </div>
        )}
        <div className="mb-6 flex flex-col items-stretch">
          <label
            className={
              "mb-2 inline-flex items-center gap-1 align-middle font-bold" +
              (errors.name ||
              (getFieldState("name").isDirty && watch("name").length <= 0)
                ? " text-warning-primary"
                : getFieldState("name").isDirty
                  ? " text-success-primary"
                  : " text-primary")
            }
            htmlFor="name"
          >
            Jméno{" "}
            {errors.name ||
            (getFieldState("name", formState).isDirty &&
              watch("name").length <= 0) ? (
              <AiFillExclamationCircle />
            ) : (
              getFieldState("name", formState).isDirty && (
                <IoIosCheckmarkCircle />
              )
            )}
          </label>
          <input
            {...register("name", { required: true })}
            className={
              "focus:shadow-outline rounded border-2 border-gray-100 px-3 py-4 " +
              "leading-tight text-black focus:outline-none" +
              (errors.name ||
              (getFieldState("name", formState).isDirty &&
                watch("name").length <= 0)
                ? " error-input-state"
                : !getFieldState("name", formState).isDirty
                  ? " input"
                  : " success-input-state")
            }
            id="name"
            type="text"
            placeholder="Jméno"
          />
          {(errors.name ??
            (getFieldState("name", formState).isDirty &&
              watch("name").length <= 0)) && (
            <span className="mt-2 text-warning-primary">
              Toto pole je povinné!
            </span>
          )}
        </div>
        {!message && (
          <div className="mb-6 flex flex-col items-stretch">
            <label
              className={
                "mb-2 inline-flex items-center gap-1 align-middle font-bold" +
                (errors.subtitle ||
                (getFieldState("subtitle", formState).isDirty &&
                  (watch("subtitle")?.length ?? 1) <= 0)
                  ? " text-warning-primary"
                  : getFieldState("subtitle", formState).isDirty
                    ? " text-success-primary"
                    : " text-primary")
              }
              htmlFor="subtitle"
            >
              Předmět{" "}
              {errors.subtitle ||
              (getFieldState("subtitle", formState).isDirty &&
                (watch("subtitle")?.length ?? 1) <= 0) ? (
                <AiFillExclamationCircle />
              ) : (
                getFieldState("subtitle", formState).isDirty && (
                  <IoIosCheckmarkCircle />
                )
              )}
            </label>
            <input
              {...register("subtitle", { required: true })}
              className={
                "focus:shadow-outline rounded border-2 border-gray-100 px-3 py-4 " +
                "leading-tight text-black focus:outline-none" +
                (errors.subtitle ||
                (getFieldState("subtitle", formState).isDirty &&
                  (watch("subtitle")?.length ?? 1) <= 0)
                  ? " error-input-state"
                  : !getFieldState("subtitle", formState).isDirty
                    ? " input"
                    : " success-input-state")
              }
              id="subject"
              type="text"
              placeholder="Předmět"
            />
            {(errors.subtitle ??
              (getFieldState("subtitle", formState).isDirty &&
                (watch("subtitle")?.length ?? 1) <= 0)) && (
              <span className="mt-2 text-warning-primary">
                Toto pole je povinné!
              </span>
            )}
          </div>
        )}
        <div className="mb-4 flex flex-col items-stretch">
          <label
            className={
              "mb-2 inline-flex items-center gap-1 align-middle font-bold" +
              (errors.message ||
              (getFieldState("message", formState).isDirty &&
                watch("message").length <= 0)
                ? " text-warning-primary"
                : getFieldState("message", formState).isDirty
                  ? " text-success-primary"
                  : " text-primary")
            }
            htmlFor="message"
          >
            Zpráva{" "}
            {errors.message ||
            (getFieldState("message", formState).isDirty &&
              watch("message").length <= 0) ? (
              <AiFillExclamationCircle />
            ) : (
              getFieldState("message", formState).isDirty && (
                <IoIosCheckmarkCircle />
              )
            )}
          </label>
          <textarea
            {...register("message", { required: true })}
            className={
              "focus:shadow-outline rounded border-2 " +
              "px-3 py-4 leading-tight" +
              "text-black focus:outline-none" +
              (errors.message ||
              (getFieldState("message", formState).isDirty &&
                watch("message").length <= 0)
                ? " custom-grabber-error error-input-state"
                : !getFieldState("message", formState).isDirty
                  ? " custom-grabber input"
                  : " custom-grabber-success success-input-state")
            }
            id="message"
            placeholder="Zpráva"
            rows={8}
          />
          {(errors.message ??
            (getFieldState("message", formState).isDirty &&
              watch("message").length <= 0)) && (
            <span className="mt-2 text-warning-primary">
              Toto pole je povinné!
            </span>
          )}
        </div>
        <button
          type="submit"
          disabled={isSubmitted}
          className={
            "btn btn-primary ripple mt-8 w-full justify-center text-center" +
            (isSubmitted ? " !bg-success-secondary" : "")
          }
        >
          {isSubmitted ? (
            "Přidávání"
          ) : (
            <>
              Přidat zprávu <ArrowRight />
            </>
          )}
        </button>
      </FullscreenModalContentWrapper>
    </>
  );
}

type Comment = components["schemas"]["GuestbookResponse"];

function CommentCollapseCard({ message }: { message: Comment }) {
  const [collapse, setCollapse] = useState(true);

  return (
    <>
      <div className="w-full rounded-[32px] bg-white p-[40px] shadow-[0_4px_16px_0_rgba(0,0,0,0.08)]">
        <div className="flex justify-between">
          <Icon>
            <GuestIcon />
          </Icon>
          <span
            onClick={() => setCollapse(!collapse)}
            className="hover:cursor-pointer"
          >
            <Icon>
              <LinkArrow
                className={
                  "arrow inline-block " +
                  (!collapse ? "-rotate-180" : "-rotate-0")
                }
              />
            </Icon>
          </span>
        </div>
        <div
          className={
            "fade-out relative flex flex-col hover:cursor-pointer" +
            (collapse ? " collapsed" : "")
          }
          onClick={() => setCollapse(!collapse)}
        >
          <h3 className="h3 !font-[800] text-primary">{message.name}</h3>
          <div className="mb-6 mt-4 text-[16px] font-[400] text-primary">
            {message.subtitle} &#8226;{" "}
            {Moment(message.createdAt).format("DD. MM. YYYY")}
          </div>
          <p className="mb-10">{message.message.replaceAll("\\n", "\n")}</p>
        </div>
      </div>
    </>
  );
}
